/**
 * CharterPrepBody — Phase-1 "Preparation Coach" chat body.
 *
 * Self-contained: reads charter prep WS state from Redux and owns its own
 * input/mention/approve UI state. It does NOT manage the WS connection or
 * render a Navbar — the host (CharterPrepPage or the in-game ChatRoom) owns
 * those. Renders the inner chat column only (messages + bottom row), matching
 * the layout of ChatRoom so it drops into either shell.
 */
import { useCallback, useMemo, useState } from 'react'
import { AlertCircle, CheckCircle2, Loader2, Sparkles, Users } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  selectPrepMessages,
  selectPrepIsConnected,
  selectPrepIsStreaming,
  selectActiveSectionKey,
  selectProfileApprovalRequired,
  selectSharingMode,
  selectSharingModeSelected,
  selectVisibleSections,
  selectPrepError,
  selectIsGeneratingProfile,
  selectProfileApproved,
  selectPrepStatus,
} from '../../redux/selectors/charterSelectors'
import { appendUserMessage } from '../../redux/slices/charterPrepWsSlice'
import {
  sendPrepMessage,
  approvePrepProfile,
} from '../../redux/asyncThunks/charterWsThunks'
import { CharterChatMessages } from '../Shared/CharterChatMessages'
import { CharterChatInput } from '../Shared/CharterChatInput'
import { toPrepDisplayMessages } from '../../utils/charterDisplayMessages'
import { useMyPrepStatus } from '../../hooks/useMyPrepStatus'

interface CharterPrepBodyProps {
  /** When true the chat is reference-only (post-ratification): no input/approve. */
  readOnly?: boolean
}

export function CharterPrepBody({ readOnly = false }: CharterPrepBodyProps) {
  const dispatch = useAppDispatch()

  const messages = useAppSelector(selectPrepMessages)
  const roomId = useAppSelector((state) => state.room.currentRoom?.roomid ?? '')
  const isConnected = useAppSelector(selectPrepIsConnected)
  const isStreaming = useAppSelector(selectPrepIsStreaming)
  const activeSectionKey = useAppSelector(selectActiveSectionKey)
  const profileApprovalRequired = useAppSelector(selectProfileApprovalRequired)
  const sharingMode = useAppSelector(selectSharingMode)
  const sharingModeSelected = useAppSelector(selectSharingModeSelected)
  const visibleSections = useAppSelector(selectVisibleSections)
  const error = useAppSelector(selectPrepError)
  const isGeneratingProfile = useAppSelector(selectIsGeneratingProfile)
  const profileApproved = useAppSelector(selectProfileApproved)
  // Only polled while the approved banner is on screen; it is the only thing
  // whose wording depends on the team.
  const myPrepStatus = useMyPrepStatus(roomId, Boolean(roomId) && profileApproved)
  const approvedWaitingText = !myPrepStatus
    ? 'Profile approved!'
    : !myPrepStatus.has_team
      ? 'Profile approved. It goes with you to your team when your instructor places you.'
      : myPrepStatus.team_size <= 1
        ? 'Profile approved. The group session opens once the rest of your team is placed.'
        : 'Profile approved! Waiting for teammates to complete their prep.'
  const prepStatus = useAppSelector(selectPrepStatus)

  const currentUser = useAppSelector((s) => s.user.user)
  const userName =
    currentUser?.name || currentUser?.email?.split('@')[0] || 'You'

  const [inputValue, setInputValue] = useState('')
  const [isMentionActive, setIsMentionActive] = useState(false)
  const [isApproving, setIsApproving] = useState(false)

  const isPrepComplete = profileApprovalRequired || prepStatus === 'completed'
  const displayMessages = useMemo(
    () => toPrepDisplayMessages(messages, userName),
    [messages, userName]
  )

  const handleSend = useCallback(() => {
    const trimmed = inputValue.trim()
    if (!trimmed || isStreaming || !isConnected) return
    const content = isMentionActive ? `@Preparation Coach ${trimmed}` : trimmed
    dispatch(
      appendUserMessage({ content, section_key: activeSectionKey ?? undefined })
    )
    dispatch(
      sendPrepMessage({
        message: content,
        section_key: activeSectionKey ?? undefined,
      })
    )
    setInputValue('')
    setIsMentionActive(false)
  }, [
    dispatch,
    inputValue,
    isStreaming,
    isConnected,
    activeSectionKey,
    isMentionActive,
  ])

  const handleApprove = async () => {
    setIsApproving(true)
    try {
      await dispatch(approvePrepProfile())
    } finally {
      setIsApproving(false)
    }
  }

  return (
    <div className='flex flex-col h-full min-h-0 py-9'>
      {/* Messages */}
      <div className='flex flex-col flex-1 min-h-0 ml-[2.5rem] px-14'>
        {!isConnected && messages.length === 0 ? (
          <div className='flex items-center justify-center flex-1 gap-2 text-gray-400'>
            <Loader2 className='h-5 w-5 animate-spin' />
            <span>Connecting to your AI coach…</span>
          </div>
        ) : (
          <CharterChatMessages
            messages={displayMessages}
            agentName='Preparation Coach'
          />
        )}
      </div>

      {/* Bottom row */}
      {!readOnly && (
        <div className='ml-[2.5rem] px-14 pt-2'>
          {error && (
            <Alert variant='destructive' className='mb-3'>
              <AlertCircle className='h-4 w-4' />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {isGeneratingProfile ? (
            <div className='flex items-center gap-3 p-4 bg-indigo-50 rounded-lg border border-indigo-200 text-sm text-indigo-700'>
              <Loader2 className='h-5 w-5 animate-spin flex-shrink-0' />
              <div>
                <p className='font-medium'>Building your prep profile…</p>
                <p className='text-xs text-indigo-500 mt-0.5'>
                  Analyzing your responses across all 11 sections. This may take
                  a moment.
                </p>
              </div>
            </div>
          ) : isPrepComplete ? (
            <div className='space-y-3'>
              {profileApproved ? (
                <div className='flex items-center gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200 text-sm text-emerald-700 font-medium'>
                  <CheckCircle2 className='h-4 w-4 flex-shrink-0' />
                  {approvedWaitingText}
                </div>
              ) : (
                <>
                  <div className='flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-700'>
                    <Users className='h-4 w-4 flex-shrink-0' />
                    Once all teammates approve, the group charter session will
                    begin automatically.
                  </div>
                  <Button
                    onClick={handleApprove}
                    disabled={
                      isApproving ||
                      !sharingModeSelected ||
                      (sharingMode === 'selective' &&
                        visibleSections.length === 0)
                    }
                    className='w-full bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50'
                    size='lg'
                  >
                    {isApproving ? (
                      <>
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        Approving…
                      </>
                    ) : (
                      'Approve My Profile & Ready Up'
                    )}
                  </Button>
                  {!sharingModeSelected && (
                    <p className='text-xs text-amber-600 text-center'>
                      Select a sharing preference in the Operations panel first.
                    </p>
                  )}
                  {sharingModeSelected &&
                    sharingMode === 'selective' &&
                    visibleSections.length === 0 && (
                      <p className='text-xs text-amber-600 text-center'>
                        Select at least one section to share in the Operations
                        panel.
                      </p>
                    )}
                </>
              )}
            </div>
          ) : (
            <>
              {/* @Preparation Coach mention chip */}
              <div className='flex items-center gap-2 mb-2'>
                <button
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                    isMentionActive
                      ? 'bg-indigo-600 border-indigo-600 text-white'
                      : 'bg-indigo-50 border-indigo-200 text-indigo-600 hover:bg-indigo-100'
                  }`}
                  onClick={() => setIsMentionActive((prev) => !prev)}
                >
                  <Sparkles className='h-3.5 w-3.5' />
                  @Preparation Coach
                </button>
              </div>

              <CharterChatInput
                value={inputValue}
                onChange={setInputValue}
                onSend={handleSend}
                disabled={!isConnected}
                isStreaming={isStreaming}
                placeholder={
                  isConnected
                    ? 'Type your message… (Enter to send, Shift+Enter for new line)'
                    : 'Connecting…'
                }
              />
              <p className='text-xs text-gray-400 mt-1 pl-1'>
                Your conversation is private. Nothing is shared without your
                consent.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  )
}
